import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Clock, MapPin, Video, Users, Globe, ArrowRight, Ticket, CheckCircle2, Pencil } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { formatEventDateTime, formatEventTime } from '@/lib/eventTime';
import EventCover from './EventCover';
import useInternalCoverBlob from './useInternalCoverBlob';
import AddToCalendar from './AddToCalendar';
import { eventTypeLabel, businessCategoryLabel, languageLabel } from './eventFormHelpers';

const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
  completed: 'bg-sky-100 text-sky-700 border-sky-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
};

function PreviewCover({ detail }) {
  const url = useInternalCoverBlob(detail?.cover_image_url);
  return (
    <div className="h-40 w-full overflow-hidden sm:h-48">
      <EventCover coverUrl={url} title={detail?.title || 'Event'} category={detail?.category} variant="hero" />
    </div>
  );
}

export default function EventPreviewModal({ eventId, summary, open, onClose, onViewEvent, onEdit, isKR }) {
  const [detail, setDetail] = useState(summary || null);
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const refetch = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [ev, summaryReg] = await Promise.all([
        calendarAPI.getEvent(eventId, { include_archived: true }).then((r) => r.data),
        calendarAPI.registrationSummary(eventId).then((r) => r.data).catch(() => null),
      ]);
      setDetail(ev);
      setReg(summaryReg);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load event'));
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [eventId, onClose]);

  useEffect(() => {
    if (open && eventId) { setDetail(summary || null); refetch(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  const my = reg?.my_registration || null;
  const isRegistered = my?.registration_status === 'registered';
  const rsvp = my?.rsvp_response;
  const requiresRsvp = detail?.requires_rsvp;
  const slotsEnabled = detail?.slots_enabled;
  const published = detail?.status === 'published';
  const cap = reg?.capacity ?? detail?.capacity ?? null;
  const remaining = reg?.remaining_seats ?? null;
  const isFull = cap != null && remaining === 0;

  const doRegister = async () => {
    setBusy(true);
    try { await calendarAPI.register(eventId, {}); toast.success('You are registered'); await refetch(); }
    catch (e) { toast.error(getErrorMessage(e, 'Registration failed')); }
    finally { setBusy(false); }
  };
  const doRsvp = async (response) => {
    setBusy(true);
    try { await calendarAPI.rsvp(eventId, response); toast.success(`RSVP: ${response}`); await refetch(); }
    catch (e) { toast.error(getErrorMessage(e, 'RSVP failed')); }
    finally { setBusy(false); }
  };

  const locationStr = detail?.physical_location
    ? [detail.physical_location.street, detail.physical_location.city, detail.physical_location.state]
        .filter(Boolean).join(', ')
    : '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg gap-0 overflow-hidden p-0" data-testid="event-preview-modal">
        {!detail ? (
          <div className="py-20 text-center text-sm text-slate-500" data-testid="event-preview-loading">Loading…</div>
        ) : (
          <>
            <PreviewCover detail={detail} />
            <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
              {/* Badges + title */}
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge className={`border text-[10px] ${STATUS_TONE[detail.status] || ''}`} data-testid="event-preview-status">{detail.status}</Badge>
                  {detail.event_type && <Badge variant="outline" className="text-[10px]">{eventTypeLabel(detail.event_type)}</Badge>}
                  {detail.business_category && <Badge variant="outline" className="border-sky-200 bg-sky-50 text-[10px] text-sky-700">{businessCategoryLabel(detail.business_category)}</Badge>}
                  {detail.visibility && (
                    <Badge variant="outline" className={`text-[10px] ${detail.visibility === 'public' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-700'}`}>
                      {detail.visibility === 'public' ? 'Public' : 'Internal'}
                    </Badge>
                  )}
                </div>
                <h2 className="text-lg font-semibold leading-snug text-slate-900" data-testid="event-preview-title">{detail.title}</h2>
              </div>

              {/* Key facts */}
              <div className="space-y-2 text-sm text-slate-700">
                <div className="flex items-start gap-2">
                  <Clock size={15} className="mt-0.5 shrink-0 text-slate-400" />
                  <span>
                    {formatEventDateTime(detail.start_datetime, detail.timezone)}
                    {detail.end_datetime && <> — {formatEventTime(detail.end_datetime, detail.timezone)}</>}
                  </span>
                </div>
                {(locationStr || detail.virtual_meeting_url) && (
                  <div className="flex items-start gap-2">
                    {detail.virtual_meeting_url ? <Video size={15} className="mt-0.5 shrink-0 text-slate-400" /> : <MapPin size={15} className="mt-0.5 shrink-0 text-slate-400" />}
                    <span className="break-words">{detail.virtual_meeting_url ? 'Virtual event' : locationStr}</span>
                  </div>
                )}
                {detail.language && (
                  <div className="flex items-center gap-2">
                    <Globe size={15} className="shrink-0 text-slate-400" />
                    <span>{languageLabel(detail.language)}</span>
                  </div>
                )}
                {cap != null && (
                  <div className="flex items-center gap-2">
                    <Users size={15} className="shrink-0 text-slate-400" />
                    <span>{remaining != null ? `${remaining} of ${cap} seats left` : `Capacity ${cap}`}</span>
                  </div>
                )}
              </div>

              {detail.description && (
                <p className="line-clamp-4 whitespace-pre-wrap text-sm text-slate-600" data-testid="event-preview-description">{detail.description}</p>
              )}

              {/* Participation state */}
              {(isRegistered || rsvp) && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="event-preview-participation">
                  <CheckCircle2 size={14} />
                  {isRegistered && <span className="font-medium">You are registered</span>}
                  {rsvp && <span className="capitalize">· RSVP: {rsvp}</span>}
                  {my?.slot_label && <span>· Slot: {my.slot_label}</span>}
                </div>
              )}

              {/* Quick RSVP for registered users */}
              {isRegistered && requiresRsvp && (
                <div className="flex flex-wrap gap-2" data-testid="event-preview-rsvp">
                  <Button size="sm" variant={rsvp === 'going' ? 'default' : 'outline'} disabled={busy} onClick={() => doRsvp('going')}>Going</Button>
                  <Button size="sm" variant={rsvp === 'maybe' ? 'default' : 'outline'} disabled={busy} onClick={() => doRsvp('maybe')}>Maybe</Button>
                  <Button size="sm" variant={rsvp === 'declined' ? 'default' : 'outline'} disabled={busy} onClick={() => doRsvp('declined')}>Decline</Button>
                </div>
              )}
            </div>

            {/* Action bar */}
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
              <Button onClick={() => onViewEvent(detail.event_id)} className="gap-1" data-testid="event-preview-view-btn">
                View Event <ArrowRight size={14} />
              </Button>

              {!isRegistered && published && slotsEnabled && (
                <Button variant="outline" onClick={() => onViewEvent(detail.event_id)} className="gap-1" data-testid="event-preview-slot-btn">
                  <Ticket size={14} /> Select Slot
                </Button>
              )}
              {!isRegistered && published && !slotsEnabled && !isFull && (
                <Button variant="outline" disabled={busy} onClick={doRegister} className="gap-1" data-testid="event-preview-participate-btn">
                  <CheckCircle2 size={14} /> {requiresRsvp ? 'RSVP' : 'Participate'}
                </Button>
              )}

              {detail.status !== 'draft' && detail.status !== 'archived' && (
                <AddToCalendar variant="internal" eventId={detail.event_id} />
              )}

              {isKR && detail.status !== 'archived' && (
                <Button variant="ghost" size="sm" className="ml-auto gap-1 text-slate-600" onClick={() => onEdit?.(detail)} data-testid="event-preview-edit-btn">
                  <Pencil size={13} /> Edit
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
